import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import {
  IndividualDashboardData,
  ManagerDashboardData,
  User,
  Team,
  Notification,
  CalendarEvent,
  ApiResponse,
  TeamMemberWorkload,
} from '@/types';

// ============================================================================
// DASHBOARD SERVICE
// ============================================================================

export const dashboardService = {
  async getIndividualDashboard(): Promise<IndividualDashboardData> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.INDIVIDUAL);
    return unwrapResponse(response) as IndividualDashboardData;
  },

  async getManagerDashboard(): Promise<ManagerDashboardData> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.MANAGER);
    return unwrapResponse(response) as ManagerDashboardData;
  },

  async getProjectManagerDashboard(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.PROJECT_MANAGER);
    return unwrapResponse(response);
  },

  async getAdminDashboard(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ADMIN);
    return unwrapResponse(response);
  },

  /** Requires teamId; backend is GET /task_management/teams/:teamId/workload */
  async getTeamWorkload(teamId: string): Promise<TeamMemberWorkload[] | unknown> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.WORKLOAD(teamId));
    return unwrapResponse(response);
  },

  /**
   * Get team members with workload based on current user's role
   * Admin: Returns all non-admin users
   * Manager: Returns only direct reports (users where MANAGER_ID = current user)
   */
  async getTeamMembersWithWorkload(): Promise<TeamMemberWorkload[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.TEAM_MEMBERS_WORKLOAD);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get filtered team members with comprehensive filters
   * Combines: department, performance, utilization, date range, project filters
   * All filters use AND logic
   */
  async getFilteredTeamMembers(filters: {
    departmentId?: string;
    minPerformance?: number; // 0-100
    maxPerformance?: number; // 0-100
    minUtilization?: number; // 0-100+
    maxUtilization?: number; // 0-100+
    dateFrom?: string; // YYYY-MM-DD
    dateTo?: string; // YYYY-MM-DD
    projectId?: string;
  }): Promise<TeamMemberWorkload[]> {
    const params = new URLSearchParams();
    if (filters.departmentId) params.set('departmentId', filters.departmentId);
    if (filters.minPerformance != null) params.set('minPerformance', String(filters.minPerformance));
    if (filters.maxPerformance != null) params.set('maxPerformance', String(filters.maxPerformance));
    if (filters.minUtilization != null) params.set('minUtilization', String(filters.minUtilization));
    if (filters.maxUtilization != null) params.set('maxUtilization', String(filters.maxUtilization));
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.projectId) params.set('projectId', filters.projectId);
    
    const url = `${API_ENDPOINTS.USERS.TEAM_MEMBERS_FILTERED}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },
};

// ============================================================================
// USER SERVICE
// ============================================================================

export const userService = {
  async getUsers(status?: 'active' | 'inactive' | 'all', options?: { page?: number; pageSize?: number; includeAdmins?: boolean }): Promise<User[]> {
    const params = new URLSearchParams();
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    if (options?.includeAdmins) params.set('includeAdmins', 'true');
    if (status != null) params.set('status', status);
    const url = params.toString() ? `${API_ENDPOINTS.USERS.LIST}?${params.toString()}` : API_ENDPOINTS.USERS.LIST;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items?: User[] }>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async getUser(userId: string): Promise<User | null> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.GET(userId));
    return unwrapResponse(response) as User | null;
  },

  async searchUsers(query: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.SEARCH, {
      params: { q: query },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getUsersByTeam(teamId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.BY_TEAM(teamId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getUsersByDepartment(departmentId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.BY_DEPARTMENT(departmentId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get users assignable to RACI (includes admins)
   * Optionally filtered by project - only project members will be returned
   */
  async getRACIAssignableUsers(projectId?: string): Promise<User[]> {
    const url = projectId 
      ? `/task_management/users/raci-assignable?projectId=${projectId}`
      : '/task_management/users/raci-assignable';
    const response = await apiClient.get(url);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async updateProfile(payload: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    timezone?: string;
    workHoursPerDay?: number;
  }): Promise<User> {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.ME, payload);
    return unwrapResponse(response) as User;
  },

  async createUser(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    role: string;
    departmentId: string;
    teamId?: string;
    managerId?: string;
    workHoursPerDay?: number;
    timezone?: string;
    isActive?: boolean;
  }): Promise<User> {
    const response = await apiClient.post(API_ENDPOINTS.USERS.CREATE, payload);
    return unwrapResponse(response) as User;
  },

  async updateUser(userId: string, payload: {
    role?: string;
    isActive?: boolean;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    departmentId?: string;
    teamId?: string | null;
    managerId?: string | null;
    workHoursPerDay?: number;
    timezone?: string;
    password?: string;
  }): Promise<User> {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.UPDATE(userId), payload);
    return unwrapResponse(response) as User;
  },

  async deleteUser(userId: string): Promise<User> {
    const response = await apiClient.delete(API_ENDPOINTS.USERS.DELETE(userId));
    return unwrapResponse(response) as User;
  },
};

// ============================================================================
// TEAM SERVICE
// ============================================================================

export const teamService = {
  async getTeams(): Promise<Team[] | { items: Team[] }> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.LIST);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : (data as { items: Team[] });
  },

  async getTeam(teamId: string): Promise<Team> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.GET(teamId));
    return unwrapResponse(response) as Team;
  },

  async getTeamMembers(teamId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.MEMBERS(teamId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getTeamWorkload(teamId: string): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.WORKLOAD(teamId));
    return unwrapResponse(response);
  },
};

// ============================================================================
// NOTIFICATION SERVICE
// ============================================================================

export const notificationService = {
  async getNotifications(): Promise<Notification[]> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.LIST);
    const data = unwrapResponse<{ items?: Notification[] } | Notification[]>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async getUnreadNotifications(): Promise<Notification[]> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.UNREAD);
    const data = unwrapResponse<{ items?: Notification[] } | Notification[]>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async markAsRead(notificationId: string): Promise<void> {
    await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
  },

  async markAllAsRead(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },

  async getPreferences(): Promise<Record<string, unknown>> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES);
    return unwrapResponse<Record<string, unknown>>(response);
  },

  async updatePreferences(preferences: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES, preferences);
    return unwrapResponse<Record<string, unknown>>(response);
  },
};

// ============================================================================
// CALENDAR SERVICE
// ============================================================================

export const calendarService = {
  /** Backend expects query params start, end (ISO date strings). */
  async getEvents(startDate: string, endDate: string): Promise<CalendarEvent[]> {
    const response = await apiClient.get(API_ENDPOINTS.CALENDAR.EVENTS, {
      params: { start: startDate, end: endDate },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getMyEvents(startDate: string, endDate: string): Promise<CalendarEvent[]> {
    const response = await apiClient.get(API_ENDPOINTS.CALENDAR.MY_EVENTS, {
      params: { start: startDate, end: endDate },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getTeamEvents(
    teamId: string,
    startDate: string,
    endDate: string
  ): Promise<CalendarEvent[]> {
    const response = await apiClient.get(
      API_ENDPOINTS.CALENDAR.TEAM_EVENTS(teamId),
      { params: { start: startDate, end: endDate } }
    );
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getHolidays(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    const response = await apiClient.get(API_ENDPOINTS.CALENDAR.HOLIDAYS, {
      params: { start: startDate, end: endDate },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async createHoliday(payload: { title: string; date: string; color?: string }): Promise<CalendarEvent> {
    const response = await apiClient.post(API_ENDPOINTS.CALENDAR.HOLIDAYS, payload);
    return unwrapResponse<CalendarEvent>(response);
  },

  async updateHoliday(
    id: string,
    payload: { title?: string; date?: string; color?: string }
  ): Promise<CalendarEvent> {
    const response = await apiClient.patch(API_ENDPOINTS.CALENDAR.HOLIDAY(id), payload);
    return unwrapResponse<CalendarEvent>(response);
  },

  async deleteHoliday(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.CALENDAR.HOLIDAY(id));
  },
};

export {
  dashboardService as default,
};
